import FormComponent from '../FormComponent';
import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import { ClassNames } from '../../utils/dom/css-classes';
import { COUNTER_CHANGE_EVENT } from '../../webComponents/Counter';

export const CounterLayouts = {
	Grid: 'grid',
	Stack: 'stack'
};

class CounterComponent extends FormComponent {
	getMaximum(option, data = this.data) {
		const maximum = option?.max ?? data?.max;

		if (maximum === '' || maximum === null || maximum === undefined) {
			return undefined;
		}

		const parsed = Number(maximum);
		return Number.isFinite(parsed) ? parsed : undefined;
	}

	getLayout() {
		return this.data?.layout === CounterLayouts.Grid ? CounterLayouts.Grid : CounterLayouts.Stack;
	}

	getCounterSections() {
		if (this.data?.sections?.length) {
			return this.data.sections;
		}

		if (this.data?.options?.length) {
			return [
				{
					title: this.data?.sectionTitle || '',
					options: this.data.options
				}
			];
		}

		return [
			{
				title: '',
				options: [
					{
						id: this.data?.id,
						label: this.data?.label || '',
						value: this.data?.value ?? 0,
						minimum: this.data?.minimum,
						max: this.data?.max,
						incrementBy: this.data?.incrementBy
					}
				]
			}
		];
	}

	renderCounter(option, data) {
		const counterId = `${data.id}_${option.id}`;
		const counterLabel = option.label || '';
		const counterValue = option.value ?? 0;
		const minimum = option.minimum ?? data.minimum ?? '';
		const maximum = this.getMaximum(option, data);
		const incrementBy = option.incrementBy ?? data.incrementBy ?? 1;
		const isGridLayout = this.getLayout() === CounterLayouts.Grid;
		const counterWrapperClass = isGridLayout
			? 'js-lta-counter l-grid__cell l-grid__cell--span-6-sm l-grid__cell--span-2-md l-grid__cell--span-6-xl u-mt-3'
			: 'js-lta-counter u-w-50 u-mt-3';

		return `
			<div class="${counterWrapperClass}">
				<lta-counter
					class="js-lta-counter-field u-mb-3"
					data-counter-id="${option.id}"
					${counterLabel ? `label="${counterLabel}"` : ''}
					value="${counterValue}"
					${minimum !== '' ? `minimum="${minimum}"` : ''}
					${maximum !== undefined ? `max="${maximum}"` : ''}
					increment-by="${incrementBy}"
					id="${counterId}"
				></lta-counter>
			</div>
		`;
	}

	renderSection(section, data) {
		const isGridLayout = this.getLayout() === CounterLayouts.Grid;
		const sectionClass = isGridLayout
			? 'lta-counter-field__section l-grid__inner u-mb-4'
			: 'lta-counter-field__section u-mb-4';
		const titleClass = isGridLayout
			? 'u-body1 u-fw-bold l-grid__cell l-grid__cell--span-6-sm l-grid__cell--span-4-md l-grid__cell--span-12-xl'
			: 'u-body1 u-fw-bold';

		return `
			<div class="${sectionClass}">
				${section.title ? `<p class="${titleClass}">${section.title}</p>` : ''}
				${section.options.map((option) => this.renderCounter(option, data)).join('')}
			</div>
		`;
	}

	markup(value, data = {}, titleMarkup) {
		const sections = this.getCounterSections();

		return `
			<div class="lta-counter-field lta-counter-field--layout-${this.getLayout()}">
				<div class="lta-counter-field__title">${titleMarkup}</div>
				${
					data.instructionText
						? `<p class="lta-text-input__label u-text-grey-lta lta-heading-subtitle u-mb-3">${data.instructionText}</p>`
						: ''
				}
				<div class="lta-counter-field__group">
					<div class="lta-counter-field__error u-mb-1 u-hidden">${data.errorMessage || 'Please enter a value'}</div>
					<div class="lta-counter-field__sections">
						${sections.map((section) => this.renderSection(section, data)).join('')}
					</div>
				</div>
			</div>
		`;
	}

	afterRender() {
		this.counterEls = SelectorEngine.find('.js-lta-counter-field', this.containerEl);
		this.errorEl = SelectorEngine.findOne('.lta-counter-field__error', this.containerEl);
		this.inputGroupEl = SelectorEngine.findOne('.lta-counter-field__group', this.containerEl);

		for (const counterEl of this.counterEls) {
			EventHandler.on(counterEl, COUNTER_CHANGE_EVENT, () => {
				this.handleCounterChange();
			});
		}

		setTimeout(() => {
			this.handleCounterChange(false);
		}, 0);
	}

	getCounterValues() {
		const values = {};

		for (const counterEl of this.counterEls) {
			const counterId = counterEl.getAttribute('data-counter-id');
			values[counterId] = Number(counterEl.getAttribute('value') ?? 0);
		}

		return values;
	}

	getCounterValueObjects() {
		const values = this.getCounterValues();
		const sections = this.getCounterSections();

		return sections.flatMap((section) =>
			section.options.map((option) => ({
				id: option.id,
				label: option.label,
				value: values[option.id] ?? option.value ?? 0,
				...(option.dependentQuestion ? { dependentQuestion: option.dependentQuestion } : {})
			}))
		);
	}

	isCounterValueEqual(previousValue, nextValue) {
		if (!Array.isArray(previousValue) || !Array.isArray(nextValue)) {
			return false;
		}

		if (previousValue.length !== nextValue.length) {
			return false;
		}

		return previousValue.every((item, index) => {
			const nextItem = nextValue[index];
			return item?.id === nextItem?.id && item?.value === nextItem?.value;
		});
	}

	handleCounterChange(shouldNotify = true) {
		const nextValue = this.getCounterValueObjects();
		const previousValue = this._value?.value;

		if (this.isCounterValueEqual(previousValue, nextValue) && this._value) {
			if (shouldNotify) {
				this.validate();
			}
			return;
		}

		const valueObject = {
			id: this.data.id,
			fieldName: this.data.fieldName,
			value: nextValue
		};

		// Always set via the value setter so FormGenerator registers the answer,
		// including the initial default when the user does not change the counter.
		this.value = valueObject;

		if (shouldNotify) {
			this.validate();
		}
	}

	validate() {
		if (this.data?.isOptional) {
			this.hideError();
			return true;
		}

		const values = this.getCounterValues();
		const sections = this.getCounterSections();
		const isValid = sections.every((section) =>
			section.options.every((option) => {
				const currentValue = values[option.id];

				if (Number.isNaN(currentValue)) {
					return false;
				}

				const minimum = option.minimum ?? this.data?.minimum;
				const maximum = this.getMaximum(option);

				if (minimum !== undefined && currentValue < minimum) {
					return false;
				}

				if (maximum !== undefined && currentValue > maximum) {
					return false;
				}

				return true;
			})
		);

		if (isValid) {
			this.hideError();
			return true;
		}

		this.showError();
		return false;
	}

	showError() {
		this.errorEl?.classList.remove(ClassNames.Hidden);
		this.inputGroupEl?.classList.add(ClassNames.Invalid, ClassNames.Focused);
	}

	hideError() {
		this.errorEl?.classList.add(ClassNames.Hidden);
		this.inputGroupEl?.classList.remove(ClassNames.Invalid, ClassNames.Focused);
	}
}

export default CounterComponent;
